import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  UploadCloud, 
  Info, 
  CheckCircle, 
  X, 
  Building2, 
  Mail, 
  Link as LinkIcon 
} from 'lucide-react';

// --- STYLING TOKENS ---
const styles = {
  container: "p-8 max-w-2xl mx-auto animate-in fade-in slide-in-from-bottom-4 duration-500",
  backBtn: "flex items-center gap-2 text-xs font-bold text-gray-400 hover:text-indigo-600 transition-colors mb-6",
  card: "bg-white rounded-2xl border border-gray-100 shadow-sm p-8",
  label: "block text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-2",
  input: "w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm transition-all outline-none focus:bg-white focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 placeholder:text-gray-300",
  helper: "text-[10px] text-gray-400 mt-1.5 flex items-center gap-1",
  btnSubmit: "w-full bg-indigo-600 text-white py-2.5 rounded-lg text-sm font-semibold hover:bg-indigo-700 active:scale-[0.98] transition-all shadow-lg shadow-indigo-200 mt-8 flex items-center justify-center gap-2",
  uploadArea: "border-2 border-dashed border-gray-100 rounded-xl p-8 flex flex-col items-center justify-center hover:border-indigo-200 hover:bg-indigo-50/30 transition-all cursor-pointer group",
};

export default function CreateCompany() {
  const navigate = useNavigate();
  const [isActive, setIsActive] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    email: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
      // Auto-generate slug from name if slug hasn't been manually edited
      slug: name === 'name' ? value.toLowerCase().replace(/\s+/g, '-') : prev.slug
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    alert(`Company "${formData.name}" created successfully!`);
    navigate('/global-admin/companies');
  };

  return (
    <div className={styles.container}>
      <button onClick={() => navigate(-1)} className={styles.backBtn}>
        <ArrowLeft size={14} /> Back to Directory
      </button>

      <div className="mb-8">
        <h1 className="font-poppins font-bold text-2xl text-gray-900">Register New Entity</h1>
        <p className="text-sm text-gray-500">Configure global settings and assign the initial super admin.</p>
      </div>

      <form onSubmit={handleSubmit} className={styles.card}>
        <div className="space-y-6">
          
          {/* Logo Upload Section */}
          <div>
            <span className={styles.label}>Company Brand Mark</span>
            <div className={styles.uploadArea}>
              <div className="p-3 bg-white rounded-full shadow-sm text-gray-400 group-hover:text-indigo-600 transition-colors">
                <UploadCloud size={24} />
              </div>
              <p className="text-xs font-semibold text-gray-600 mt-3">Click to upload logo</p>
              <p className="text-[10px] text-gray-400 mt-1">PNG, JPG or SVG (max. 400x400px)</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Company Name */}
            <div>
              <label className={styles.label}>Legal Company Name</label>
              <div className="relative">
                <Building2 size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input 
                  type="text" 
                  name="name"
                  placeholder="e.g. Acme Global Ltd"
                  className={`${styles.input} pl-9`}
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>

            {/* Company Slug */}
            <div>
              <label className={styles.label}>URL Identifier (Slug)</label>
              <div className="relative">
                <LinkIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input 
                  type="text" 
                  name="slug"
                  placeholder="acme-global"
                  className={`${styles.input} pl-9`}
                  value={formData.slug}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <p className={styles.helper}><Info size={10}/> Used for tenant subdomains</p>
            </div>
          </div>

          {/* Admin Email */}
          <div>
            <label className={styles.label}>Initial Super Admin Email</label>
            <div className="relative">
              <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input 
                type="email" 
                name="email"
                placeholder="admin@company.com"
                className={`${styles.input} pl-9`}
                value={formData.email}
                onChange={handleInputChange}
                required
              />
            </div>
            <p className={styles.helper}>An invitation link will be sent to this address.</p>
          </div>

          {/* Status Toggle */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-100">
            <div>
              <p className="text-sm font-bold text-gray-800">Operational Status</p>
              <p className="text-[11px] text-gray-400">Enable or disable platform access immediately.</p>
            </div>
            <button 
              type="button"
              onClick={() => setIsActive(!isActive)}
              className={`w-12 h-6 rounded-full transition-all relative ${isActive ? 'bg-emerald-500' : 'bg-gray-300'}`}
            >
              <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${isActive ? 'left-7' : 'left-1'}`} />
            </button>
          </div>

        </div>

        <button type="submit" className={styles.btnSubmit}>
          <CheckCircle size={16} /> Create Company Profile
        </button>
      </form>

      <div className="mt-8 text-center">
        <p className="text-[11px] text-gray-400 uppercase font-bold tracking-widest">
          Platform Infrastructure Group
        </p>
      </div>
    </div>
  );
}